import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface ReminderRule {
  id?: string;
  riskLevel: 'low' | 'medium' | 'high';
  timingHours: number;
  channels: string[];
  enabled: boolean;
}

export interface ReminderSettings {
  lowRisk: { enabled: boolean; reminders: Array<{ timing: string; channels: string[] }> };
  mediumRisk: { enabled: boolean; reminders: Array<{ timing: string; channels: string[] }> };
  highRisk: { enabled: boolean; reminders: Array<{ timing: string; channels: string[] }> };
}

const TIMING_LABEL_TO_HOURS: Record<string, number> = {
  '1h': 1, '2h': 2, '4h': 4, '12h': 12, '24h': 24, '48h': 48, '72h': 72,
};

const HOURS_TO_TIMING_LABEL: Record<number, string> = {
  1: '1h', 2: '2h', 4: '4h', 12: '12h', 24: '24h', 48: '48h', 72: '72h',
};

const RISK_LEVEL_MAP: Record<string, 'low' | 'medium' | 'high'> = {
  lowRisk: 'low', mediumRisk: 'medium', highRisk: 'high',
};

const RISK_LEVEL_REVERSE: Record<string, string> = {
  low: 'lowRisk', medium: 'mediumRisk', high: 'highRisk',
};

export const useReminderRules = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const defaultSettings: ReminderSettings = {
    lowRisk: { enabled: true, reminders: [{ timing: '24h', channels: ['email'] }] },
    mediumRisk: {
      enabled: true,
      reminders: [
        { timing: '48h', channels: ['email'] },
        { timing: '2h', channels: ['sms'] },
      ],
    },
    highRisk: {
      enabled: true,
      reminders: [
        { timing: '72h', channels: ['email', 'sms'] },
        { timing: '24h', channels: ['sms'] },
        { timing: '1h', channels: ['sms'] },
      ],
    },
  };

  const [settings, setSettings] = useState<ReminderSettings>(defaultSettings);

  useEffect(() => {
    if (user) loadRules();
  }, [user]);

  const saveDefaultRules = async () => {
    if (!user) return;
    const rows: Array<{ user_id: string; risk_level: string; timing_hours: number; channels: string[]; enabled: boolean }> = [];
    for (const [uiKey, config] of Object.entries(defaultSettings)) {
      const riskLevel = RISK_LEVEL_MAP[uiKey];
      if (!riskLevel) continue;
      for (const reminder of config.reminders) {
        const hours = TIMING_LABEL_TO_HOURS[reminder.timing];
        if (!hours) continue;
        rows.push({ user_id: user.id, risk_level: riskLevel, timing_hours: hours, channels: reminder.channels, enabled: config.enabled });
      }
    }
    if (rows.length > 0) {
      await supabase.from('tenant_reminder_rules').insert(rows);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tenant_reminder_rules')
        .select('*')
        .order('risk_level')
        .order('timing_hours', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        await saveDefaultRules();
        setLoading(false);
        return;
      }

      const built: ReminderSettings = {
        lowRisk: { enabled: false, reminders: [] },
        mediumRisk: { enabled: false, reminders: [] },
        highRisk: { enabled: false, reminders: [] },
      };

      for (const row of data) {
        const key = RISK_LEVEL_REVERSE[row.risk_level] as keyof ReminderSettings;
        if (!key) continue;
        if (row.enabled) built[key].enabled = true;
        built[key].reminders.push({
          timing: HOURS_TO_TIMING_LABEL[row.timing_hours] || `${row.timing_hours}h`,
          channels: row.channels || [],
        });
      }

      for (const key of Object.keys(built) as Array<keyof ReminderSettings>) {
        if (built[key].reminders.length === 0) {
          built[key] = defaultSettings[key];
        }
      }

      setSettings(built);
    } catch (err) {
      console.error('Error loading reminder rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveRules = async (newSettings: ReminderSettings): Promise<boolean> => {
    if (!user) return false;
    setSaving(true);
    try {
      await supabase
        .from('tenant_reminder_rules')
        .delete()
        .eq('user_id', user.id);

      const rows: Array<{
        user_id: string;
        risk_level: string;
        timing_hours: number;
        channels: string[];
        enabled: boolean;
      }> = [];

      for (const [uiKey, config] of Object.entries(newSettings)) {
        const riskLevel = RISK_LEVEL_MAP[uiKey];
        if (!riskLevel) continue;
        for (const reminder of config.reminders) {
          const hours = TIMING_LABEL_TO_HOURS[reminder.timing] || parseInt(reminder.timing);
          if (!hours) continue;
          rows.push({
            user_id: user.id,
            risk_level: riskLevel,
            timing_hours: hours,
            channels: reminder.channels,
            enabled: config.enabled,
          });
        }
      }

      if (rows.length > 0) {
        const { error } = await supabase.from('tenant_reminder_rules').insert(rows);
        if (error) throw error;
      }

      setSettings(newSettings);
      return true;
    } catch (err) {
      console.error('Error saving reminder rules:', err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { settings, setSettings, loading, saving, saveRules, loadRules };
};
